'use client';

import { useReducer } from 'react';

import type { LoyaltyMemberPoints } from '@libs/common-types';

import type { TooltipContentfulData } from '@/components/OnboardingTooltips/types';
import type { TreatsMenuTabProps } from '@/components/TreatsMenu/TreatsMenu';
import type { PageDataSignedInAll } from '@/types/TreatsRewards';

import type { TreatsContextProps, TreatsDispatch } from './TreatsProvider';

/** ------------------------------------
 * state
 * ---------------------------------- */

export interface TreatsStateProps {
    currentTierIndex: number;
    menu?: TreatsMenuTabProps[];
    menuActiveIndex: number;
    pages?: PageDataSignedInAll;
    /** TODO: this is a placeholder to show tooltipData to the user or not.  State will be set to match flag returned via an API call.  Can likely be combined with tooltipData state for clarity */
    showOnboarding?: boolean;
    tooltipData?: TooltipContentfulData[];
}

export const initialTreatsState: TreatsStateProps = {
    currentTierIndex: 0,
    menu: undefined,
    menuActiveIndex: 0,
    pages: undefined,
    showOnboarding: false,
    tooltipData: undefined,
};

/** ------------------------------------
 * helpers
 * ---------------------------------- */

export function getCurrentTierIndex(memberPoints?: LoyaltyMemberPoints): number {
    return memberPoints?.currentTierRank ? memberPoints.currentTierRank - 1 : 0;
}

/** ------------------------------------
 * reducer
 * ---------------------------------- */

export enum TreatsActionType {
    setMemberPoints = 'setMemberPoints',
    setShowOnboarding = 'setShowOnboarding',
    setMenuActiveIndex = 'setMenuActiveIndex',
}

export type TreatsActionPayloads = {
    setShowOnboarding: {
        type: TreatsActionType.setShowOnboarding;
        payload: boolean;
    };
    setMenuActiveIndex: {
        type: TreatsActionType.setMenuActiveIndex;
        payload: number;
    };
};

export type TreatsAction = TreatsActionPayloads[keyof TreatsActionPayloads];

function treatsReducer(state: TreatsStateProps, action: TreatsAction): TreatsStateProps {
    switch (action.type) {
        case TreatsActionType.setShowOnboarding: {
            return {
                ...state,
                showOnboarding: action.payload,
            };
        }

        case TreatsActionType.setMenuActiveIndex: {
            return {
                ...state,
                menuActiveIndex: action.payload,
            };
        }

        default:
            return state;
    }
}

/** ------------------------------------
 * actions
 * ---------------------------------- */

export const treatsActions = {
    setShowOnboarding(dispatch: TreatsDispatch, payload: TreatsActionPayloads['setShowOnboarding']['payload']): void {
        dispatch({
            type: TreatsActionType.setShowOnboarding,
            payload,
        });
    },

    setMenuActiveIndex(dispatch: TreatsDispatch, payload: TreatsActionPayloads['setMenuActiveIndex']['payload']): void {
        dispatch({
            type: TreatsActionType.setMenuActiveIndex,
            payload,
        });
    },
};

/** ------------------------------------
 * hook
 * ---------------------------------- */

export type UseTreatsReducerReturn = [TreatsContextProps['treatsAppState'], TreatsContextProps['dispatch']];

export function useTreatsReducer({ menu, pages, tooltipData }: Partial<TreatsStateProps>): UseTreatsReducerReturn {
    const [treatsAppState, dispatch] = useReducer(treatsReducer, {
        ...initialTreatsState,
        menu,
        pages,
        tooltipData,
    });

    return [treatsAppState, dispatch];
}
